import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { LMSCourse } from '@nsc/simple-lms/entities/LMSCourse.js';
import { LMSCourseEnrollment } from '@nsc/simple-lms/entities/LMSCourseEnrollment.js';
import { SmartTeamsPartnerCompany } from '../db/SmartTeamsCompany.js';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';

// Server-side CSV export of field engineers with their enrollment scores.
//
// The previous implementation ran entirely in the browser: it pulled every field engineer via the
// generic `getAllEntities` with nested `partnerCompanies`/`enrollments` selection sets, which
// resolved a per-field-engineer `enrollments` join (and a `course` lookup per enrollment that the
// CSV never used) and shipped the whole nested graph to the client to assemble there. At production
// volume (~6k engineers, ~6k enrollments) that request hangs.
//
// Instead we read the three collections in bulk (field engineers, partner companies, enrollments),
// join them in memory, and return the finished CSV as a single string. Only the columns the CSV
// needs are projected — in particular attempt `answers` (potentially large) are never loaded.

// Mirror of @nsc/simple-lms/client/score-utils.ts, inlined so this server operation carries no
// dependency on a client-side module. Keep the two in sync.
const clampScorePercent = (value: number | null | undefined): number => {
  if (value === null || value === undefined || Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, value));
};

// Mirror of @nsc/smartteams/client/date-format.ts `formatDateTime` (`d/m/Y H:i:s`, server-local
// time), inlined for the same reason as clampScorePercent — no client-module dependency here.
// Returns '' for a missing/invalid date.
const pad = (value: number): string => String(value).padStart(2, '0');
const formatDateTime = (value: Date | null | undefined): string => {
  if (value == null) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export const exportFieldEngineersCsv = registerMutation({
  operationName: 'exportFieldEngineersCsv',
  type: () => String,
  params: [],
  resolve: () => async (): Promise<string> => {
    const canExport = await accessController.can(createPermissionPayload(SmartTeamsPermission.FEImportExportAdmin));
    if (!canExport) {
      throw new ExpectedError('No permission to import or export field engineers');
    }

    const fieldEngineerCollection = await getRawCollection<SmartTeamsFieldEngineer>(
      SmartTeamsFieldEngineer._collectionName,
    );
    const companyCollection = await getRawCollection<SmartTeamsPartnerCompany>(
      SmartTeamsPartnerCompany._collectionName,
    );
    const enrollmentCollection = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);
    const courseCollection = await getRawCollection<LMSCourse>(LMSCourse._collectionName);

    const [fieldEngineers, companies, enrollments, courses] = await Promise.all([
      // `deletedAt: null` matches both null and missing, so it keeps live field engineers only.
      fieldEngineerCollection
        .find({ deletedAt: null }, { projection: { name: 1, email: 1, partnerCompanyIds: 1, userId: 1 } })
        .toArray(),
      companyCollection.find({}, { projection: { name: 1 } }).toArray(),
      enrollmentCollection
        .find(
          {},
          {
            projection: {
              userId: 1,
              courseId: 1,
              'attempts.scorePercent': 1,
              'attempts.startedAt': 1,
              'attempts.completedAt': 1,
              'attempts.ip': 1,
            },
          },
        )
        .toArray(),
      courseCollection.find({}, { projection: { title: 1 } }).toArray(),
    ]);

    const companyNameById = new Map(companies.map(company => [company._id, company.name] as const));
    const courseTitleById = new Map(courses.map(course => [course._id, course.title] as const));

    // Group enrollments by their `userId` — the key a field engineer joins on (see the `enrollments`
    // DataLoadedField in db/SmartTeamsFieldEngineer.ts).
    const enrollmentsByUserId = new Map<string, LMSCourseEnrollment[]>();
    for (const enrollment of enrollments) {
      if (!enrollment.userId) continue;
      const existing = enrollmentsByUserId.get(enrollment.userId);
      if (existing) existing.push(enrollment);
      else enrollmentsByUserId.set(enrollment.userId, [enrollment]);
    }

    const headers = ['Name', 'Email', 'Partner Name', 'Scores (All Tries)'];
    const rows = [headers.join(',')];

    for (const fe of fieldEngineers) {
      const partnerName =
        (fe.partnerCompanyIds ?? [])
          .filter(id => companyNameById.has(id))
          .map(id => companyNameById.get(id))
          .join(', ') || 'Unknown Partner';

      const feEnrollments = fe.userId ? (enrollmentsByUserId.get(fe.userId) ?? []) : [];
      // One group per course, e.g.
      //   "Nutanix: 80% (started: 22/07/2026 15:24:08, completed: 22/07/2026 15:31:02, ip: 1.2.3.4), 100% (…)"
      // Tries joined by ", ", courses joined by "; ". Each try appends whichever of started/completed/ip
      // exist (attempts recorded before start-time / IP tracking carry only what they have). Courses with
      // no completed attempt are skipped so the cell only lists scores that exist.
      const courseScoreGroups = feEnrollments
        .map(enrollment => {
          const tries = (enrollment.attempts ?? [])
            .filter(attempt => attempt.scorePercent !== undefined && attempt.completedAt)
            .map(attempt => {
              const score = `${clampScorePercent(attempt.scorePercent)}%`;
              const detailParts = [
                attempt.startedAt ? `started: ${formatDateTime(attempt.startedAt)}` : null,
                attempt.completedAt ? `completed: ${formatDateTime(attempt.completedAt)}` : null,
                attempt.ip ? `ip: ${attempt.ip}` : null,
              ].filter((part): part is string => part !== null);
              return detailParts.length ? `${score} (${detailParts.join(', ')})` : score;
            });
          if (!tries.length) return null;
          const courseTitle =
            (enrollment.courseId ? courseTitleById.get(enrollment.courseId) : null) || 'Unknown Course';
          return `${courseTitle}: ${tries.join(', ')}`;
        })
        .filter((group): group is string => group !== null);
      const scoresText = courseScoreGroups.length ? courseScoreGroups.join('; ') : 'No scores';

      rows.push([`"${fe.name || ''}"`, fe.email || '', `"${partnerName}"`, `"${scoresText}"`].join(','));
    }

    return rows.join('\n');
  },
});
